/**
 * PrototypeShell: /prototypes/:id, the Prototype detail surface.
 *
 * One `.page` column on the queue grammar: a ListHead (the title, the Agent
 * credit, the way back to the ticket, the viewport toggles: Mobile 390,
 * Tablet 768, Full fluid), then the sandboxed PrototypeFrame, the revision
 * timeline (notes as labels, mono seq numbers, latest selected by default) and
 * the share panel (mint, copy, revoke, who and when). The Ask dock is bound to
 * the prototype's own parent-typed conversation (parentType "prototype",
 * upserted by PrototypeChatRail), so revising continues in place.
 *
 * Glass & Print: a Prototype is staged work waiting on a human, so the frame
 * and the share gate render as glass; the revision history is the printed
 * record. There is no nav entry: this URL is reached from chat cards, case
 * files, and citations.
 */
import { useEffect, useState, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { Prototype, PrototypeRevision } from "@yagni/shared";
import { workItemUrl } from "@yagni/shared";
import { Btn, ListHead } from "../queue/index.js";
import { SurfaceSkeleton } from "../ui/index.js";
import { PrototypeReview } from "./PrototypeReview.js";
import { VIEWPORT_WIDTH, ViewportToggle, type Viewport } from "./ViewportToggle.js";
import { PrototypeChatRail } from "./PrototypeChatRail.js";
import { RevisionTimeline } from "./RevisionTimeline.js";
import { SharePanel, type ShareState } from "./SharePanel.js";
import { captureUiError } from "../sentry.js";

/**
 * Refreshes that fail in a row before the page reports it is not updating.
 * One miss is a blip (a deploy, a reconnect); three is a page showing stale
 * revisions and sharing. An outage that goes on is reported again every
 * REFRESH_FAILURES_REREPORTED misses (five minutes at one refresh per 5s).
 */
const REFRESH_FAILURES_REPORTED = 3;
const REFRESH_FAILURES_REREPORTED = 60;

/**
 * The detail endpoint's prototype row. Share facts beyond the pinned shared
 * `Prototype` type (the owner-visible share URL and its mint provenance) are
 * read defensively — the panel renders whatever the API provides.
 */
type PrototypeDetail = Prototype & {
  shareUrl?: string | null;
  shareCreatedAt?: string | null;
  shareCreatedBy?: string | null;
  shareCreatedByName?: string | null;
};

/** The share facts a detail read carries, as the share panel shows them. */
function shareFromDetail(proto: PrototypeDetail): ShareState {
  return {
    shared: proto.shared,
    url: proto.shareUrl ?? null,
    createdAt: proto.shareCreatedAt ?? null,
    createdByName: proto.shareCreatedByName ?? null,
  };
}

/** 'wright' → 'Wright': worker slugs are lowercase guild surnames. */
function workerDisplayName(slug: string): string {
  return slug.charAt(0).toUpperCase() + slug.slice(1);
}

export function PrototypeShell() {
  const { prototypeId } = useParams<{ prototypeId: string }>();
  const [searchParams] = useSearchParams();
  const requestedRevision = searchParams.get("rev");
  const requestedSeq = requestedRevision != null && /^[1-9]\d{0,2}$/.test(requestedRevision) && Number(requestedRevision) <= 100
    ? Number(requestedRevision) : null;

  const [prototype, setPrototype] = useState<PrototypeDetail | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "missing">(
    "loading",
  );
  const [revisions, setRevisions] = useState<PrototypeRevision[]>([]);
  const [selectedSeq, setSelectedSeq] = useState<number | null>(null);
  const [viewport, setViewport] = useState<Viewport>("full");

  const [share, setShare] = useState<ShareState>({
    shared: false,
    url: null,
    createdAt: null,
    createdByName: null,
  });

  // ── detail + revision index ──
  useEffect(() => {
    if (!prototypeId) return;
    let cancelled = false;
    setLoadState("loading");
    setPrototype(null);
    setRevisions([]);
    setSelectedSeq(null);
    void (async () => {
      try {
        const [detailRes, revsRes] = await Promise.all([
          fetch(`/api/prototypes/${encodeURIComponent(prototypeId)}`, {
            credentials: "include",
          }),
          fetch(`/api/prototypes/${encodeURIComponent(prototypeId)}/revisions`, {
            credentials: "include",
          }),
        ]);
        if (cancelled) return;
        if (!detailRes.ok) {
          setLoadState("missing");
          return;
        }
        const detailBody = (await detailRes.json()) as
          | { prototype?: PrototypeDetail }
          | PrototypeDetail;
        const proto =
          "prototype" in detailBody && detailBody.prototype
            ? detailBody.prototype
            : (detailBody as PrototypeDetail);
        setPrototype(proto);
        setShare(shareFromDetail(proto));
        if (revsRes.ok) {
          const revsBody = (await revsRes.json()) as
            | { revisions?: PrototypeRevision[] }
            | PrototypeRevision[];
          const rows = Array.isArray(revsBody)
            ? revsBody
            : revsBody.revisions ?? [];
          setRevisions(
            [...rows].sort((a, b) => b.seq - a.seq),
          );
        }
        setLoadState("ready");
      } catch {
        if (!cancelled) setLoadState("missing");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [prototypeId, requestedRevision]);

  // Latest is the default selection; selecting a timeline row overrides it.
  const activeSeq = selectedSeq ?? requestedSeq ?? prototype?.currentRevision ?? null;
  const requestedRevisionMissing = requestedRevision != null && (requestedSeq == null || !revisions.some(revision => revision.seq === requestedSeq));

  useEffect(() => {
    if (!prototypeId) return;
    const controller = new AbortController();
    let failures = 0;
    const failed = (error: unknown) => {
      if (controller.signal.aborted) return;
      failures += 1;
      const due =
        failures >= REFRESH_FAILURES_REPORTED &&
        (failures - REFRESH_FAILURES_REPORTED) % REFRESH_FAILURES_REREPORTED === 0;
      if (!due) return;
      captureUiError(error, {
        area: "prototypes",
        operation: "prototype_page_refresh",
        severity: "warning",
        expected: false,
      });
    };
    const timer = setInterval(() => {
      if (document.visibilityState === "hidden") return;
      void Promise.all([
        fetch(`/api/prototypes/${encodeURIComponent(prototypeId)}`, { credentials: "include", signal: controller.signal }),
        fetch(`/api/prototypes/${encodeURIComponent(prototypeId)}/revisions`, { credentials: "include", signal: controller.signal }),
      ]).then(async ([detail, index]) => {
        if (!detail.ok || !index.ok) {
          failed(new Error(`Prototype ${prototypeId} refresh answered ${detail.status}/${index.status}`));
          return;
        }
        const [body, rows] = await Promise.all([detail.json(), index.json()]);
        if (controller.signal.aborted) return;
        const proto = (body.prototype ?? body) as PrototypeDetail;
        setPrototype(proto);
        // A link minted or revoked elsewhere (an agent's share, a detach)
        // reaches the panel on the next refresh.
        setShare(shareFromDetail(proto));
        setRevisions([...(Array.isArray(rows) ? rows : rows.revisions ?? [])].sort((a: PrototypeRevision, b: PrototypeRevision) => b.seq - a.seq));
        failures = 0;
      }).catch((error: unknown) => {
        // Keep the last loaded revision; report only a persistent failure,
        // naming the page that was polling.
        failed(new Error(`Prototype ${prototypeId} refresh failed: ${error instanceof Error ? error.message : String(error)}`));
      });
    }, 5000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [prototypeId]);

  // ── header: the title, its facts, the way back to the ticket and the
  // viewport toggles. A ticket's prototype leads back to the ticket on Work:
  // the design is evidence for that plan, so the way out is the case file.
  const navigate = useNavigate();
  const workItemId = prototype?.workItemId ?? null;
  const sub: ReactNode[] = ["Prototype"];
  if (prototype?.creditedWorker) {
    // Worker credit: a machine fact, mono voice. The detail payload carries
    // no team name, so the line stays worker-only.
    sub.push(
      <span data-testid="prototype-credit">{workerDisplayName(prototype.creditedWorker)} prototyped this</span>,
    );
  }
  const header = (
    <ListHead
      title={prototype?.title ?? "Prototype"}
      sub={sub}
      acts={
        <>
          {workItemId && (
            <Btn variant="quiet" size="sm" onClick={() => navigate(workItemUrl(workItemId))}>
              Open the ticket
            </Btn>
          )}
          <ViewportToggle value={viewport} onChange={setViewport} />
        </>
      }
    />
  );

  // ── body ──
  let body: ReactNode;
  if (loadState === "loading") {
    body = <SurfaceSkeleton label="Reading" lens="prototype" testId="prototype-loading" />;
  } else if (loadState === "missing" || !prototype) {
    body = (
      <div className="empty dashed" data-testid="prototype-missing">
        <h3>This prototype is not on file.</h3>
        <p>It may have been removed, or the link may be stale.</p>
      </div>
    );
  } else if (requestedRevisionMissing) {
    body = (
      <div className="empty dashed tight" role="alert">
        This prototype revision is not on file.
      </div>
    );
  } else {
    body = (
      <>
        {prototypeId && activeSeq != null && <PrototypeReview key={prototypeId} prototypeId={prototypeId} title={prototype.title} revision={activeSeq} onStartFeedback={() => setSelectedSeq(activeSeq)} revisions={revisions.map(rev => rev.seq)} width={VIEWPORT_WIDTH[viewport]} parts={revisions.find(rev => rev.seq === activeSeq)?.parts ?? null} />}

        <div className="grid gap-[var(--gap-page)] md:grid-cols-2 items-start">
          <RevisionTimeline revisions={revisions} activeSeq={activeSeq} onSelect={setSelectedSeq} />
          {prototypeId && (
            <SharePanel
              prototypeId={prototypeId}
              share={share}
              onShareChange={setShare}
              workItemId={prototype.workItemId ?? null}
            />
          )}
        </div>
      </>
    );
  }

  return (
    <div className="page prototype" data-testid="prototype-page">
      {prototype && <PrototypeChatRail prototype={prototype} />}
      {header}
      {body}
    </div>
  );
}
